import {
  ButtonGroup,
  Card,
  Choice,
  DataStatus,
  openDialog,
  Alert,
  OptionTypeBase,
} from 'platform/components';
import {VStack, Show, HStack, Box} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {always, groupBy, indexBy, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useLazyDownloadTemplateV3Query} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError, useBranches, useGetCurrentBranch} from '@dms/shared';

import {downloadBlobFile, getSafely, Nullish} from 'shared/web';

import {GroupDefinition} from '../types/Definition';
import {getAuthorizationProfileOptionLabelWithCount} from '../utils/getAuthorizationProfileOptionLabelWithCount';
import {CreateTemplateForm} from './CreateTemplateForm';
import {TemplatesTable} from './TemplatesTable';

interface AuthorizationProfileTabProps {
  definition: GroupDefinition;
}

export function AuthorizationProfileCard(props: AuthorizationProfileTabProps) {
  const [selectedAuthorizationProfileId, setSelectedAuthorizationProfileId] = useState<
    string | null
  >(null);

  const {data: branches} = useBranches();
  const {activeBranchId} = useGetCurrentBranch();
  const branchesById = indexBy((branch) => branch.id, branches?.branchListItems ?? []);
  const hasSomeProfileWithAuthorizationId = props.definition.authorizationProfiles.some((item) =>
    isNotNil(item.authorizationProfile.authorizationProfileId)
  );

  const [downloadTemplate] = useLazyDownloadTemplateV3Query();

  const profilesByBranchId = groupBy(
    ({authorizationProfile}) => authorizationProfile.branchId ?? 'default',
    props.definition.authorizationProfiles
  );

  const getBranchName = (branchId: string) => {
    const branch = getSafely(branchesById, branchId);
    return (
      branch?.marketingName ?? branch?.companyName ?? i18n.t('entity.vehicle.labels.otherLabel')
    );
  };

  const profileOptions = Object.entries(profilesByBranchId)
    .map(([branchId, profiles]) => {
      const id = branchId === 'default' ? null : branchId;
      const label = match([id, activeBranchId])
        .with(
          [null, Pattern.any],
          always(i18n.t('entity.authorizationProfiles.labels.withoutAuthorization'))
        )
        .when(
          ([id, activeBranchId]) => id === activeBranchId,
          () => `${getBranchName(activeBranchId)} - ${i18n.t('page.settings.labels.currentBranch')}`
        )
        .otherwise(() => getBranchName(branchId));

      return {
        label,
        value: id,
        options: profiles?.map(({authorizationProfile}) => ({
          label: authorizationProfile.title,
          value: authorizationProfile.authorizationProfileId,
        })),
      };
    })
    .sort((a, b) =>
      match([a.value, b.value])
        .with([null, null], () => 0)
        .with([null, Pattern.string], () => -1)
        .with([Pattern.string, null], () => 1)
        .with([activeBranchId, Pattern.string], () => -1)
        .with([Pattern.string, activeBranchId], () => 1)
        .otherwise(() => a.label.localeCompare(b.label))
    );

  const selectedAuthorizationProfile = props.definition.authorizationProfiles.find(
    (item) => item.authorizationProfile.authorizationProfileId === selectedAuthorizationProfileId
  );

  const primaryTemplate = selectedAuthorizationProfile?.templates.find(
    (template) => template.isPrimary
  );

  const handleDownloadPrimary = () => {
    if (isNotNil(primaryTemplate)) {
      downloadTemplate({templateId: primaryTemplate.id})
        .unwrap()
        .then((file) =>
          downloadBlobFile(file, {fileName: primaryTemplate.title, fileType: props.definition.type})
        )
        .catch(handleApiError);
    }
  };

  const handleAddNewTemplate = () =>
    openDialog(
      <CreateTemplateForm
        authorizationProfileId={
          selectedAuthorizationProfile?.authorizationProfile.authorizationProfileId ?? null
        }
        documentCode={props.definition.documentCode}
        documentType={props.definition.type}
      />,
      {title: i18n.t('general.actions.addNewTemplate')}
    );

  const formatOptionLabel = (option: OptionTypeBase<string | Nullish>) =>
    getAuthorizationProfileOptionLabelWithCount(option, props.definition.authorizationProfiles);

  return (
    <Card
      key={props.definition.id}
      title={props.definition.title}
      subtitle={`(${props.definition.templates.length})`}
      variant="inlineWhite"
      isExpandable
      isClosedByDefault
      flags={[
        {
          label: props.definition.type,
          isSubtle: true,
          colorScheme: props.definition.type === 'docx' ? 'orange' : 'blue',
        },
      ]}
    >
      <VStack spacing={4}>
        <HStack justify="space-between" spacing={2}>
          <Show when={hasSomeProfileWithAuthorizationId}>
            <Box flex={1} maxWidth={150}>
              <Choice
                options={profileOptions}
                value={selectedAuthorizationProfileId}
                onChange={setSelectedAuthorizationProfileId}
                formatOptionLabel={formatOptionLabel}
                menuInPortal
                isNotClearable
                isSearchable
                placeholder={i18n.t('entity.header.labels.withoutAuthorization')}
              />
            </Box>
          </Show>

          <ButtonGroup
            buttons={[
              {
                title: i18n.t('entity.document.actions.downloadPrimary'),
                leftIcon: 'file/download',
                onClick: handleDownloadPrimary,
                isDisabled: isNil(primaryTemplate),
                variant: 'ghostLink',
              },
              {
                title: i18n.t('general.actions.addNewTemplate'),
                leftIcon: 'content/add_circle',
                onClick: handleAddNewTemplate,
                variant: 'ghostLink',
              },
            ]}
          />
        </HStack>

        <Show when={selectedAuthorizationProfileId === null}>
          <Alert
            variant="info"
            message={i18n.t('entity.header.labels.withoutAuthorizationLabel')}
          />
        </Show>

        <DataStatus
          isEmpty={isNilOrEmpty(selectedAuthorizationProfile?.templates)}
          spacing={10}
          emptyMessage={i18n.t('entity.document.labels.noTemplatesForAuthProfile')}
          action={{
            title: i18n.t('general.actions.addNewTemplate'),
            leftIcon: 'content/add_circle',
            onClick: handleAddNewTemplate,
          }}
        >
          <TemplatesTable
            definitionType={props.definition.type}
            authorizationProfile={selectedAuthorizationProfile}
            documentCode={props.definition.documentCode}
          />
        </DataStatus>
      </VStack>
    </Card>
  );
}
